/* eslint-disable no-console */
import {
  Divider,
  Typography,
  Box,
  ExpansionPanel,
  Grid,
  ExpansionPanelSummary,
  ExpansionPanelDetails,
} from "/vendor/@material-ui/core";
import React, { useEffect, useState } from "react";
import { ExpandMoreIcon } from "/lib/component/icon";
import axios from "axios";

interface TransactionRecord {
  _field: string;
  _measurement: string;
  _start: string;
  _stop: string;
  _time: string;
  _value: number;
  check_name: string;
  entity_name: string;
  namespace: string;
  result: string;
  table: number;
}

interface TransactionData {
  ticketSold: TransactionRecord[];
  voucherCashed: TransactionRecord[];
  loading: boolean;
  error: string | null;
}

export function RecentTransactions({
  name,
  namespace,
}: {
  name: string;
  namespace: string;
}) {
  const [transactionData, setTransactionData] = useState<TransactionData>({
    ticketSold: [],
    voucherCashed: [],
    loading: true,
    error: null,
  });

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getLatestRecords = (records: TransactionRecord[]) => {
    return records
      .sort((a, b) => new Date(b._time).getTime() - new Date(a._time).getTime())
      .slice(0, 10);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setTransactionData((prev) => ({ ...prev, loading: true, error: null }));

        const ticketResponse = await axios.get(
          `https://api.srv-metricslayer.hardware-inno.gcpprodau.tbm.sh/metrics/${namespace}/${name}/ticket-cost`,
        );
        const voucherResponse = await axios.get(
          `https://api.srv-metricslayer.hardware-inno.gcpprodau.tbm.sh/metrics/${namespace}/${name}/cashing`,
        );

        setTransactionData({
          ticketSold: Array.isArray(ticketResponse.data)
            ? ticketResponse.data
            : [],
          voucherCashed: Array.isArray(voucherResponse.data)
            ? voucherResponse.data
            : [],
          loading: false,
          error: null,
        });
      } catch (error) {
        console.error("Error fetching transaction data:", error);
        setTransactionData((prev) => ({
          ...prev,
          loading: false,
          error: "Failed to fetch transaction data. Please try again later.",
        }));
      }
    };

    fetchData();
  }, [name, namespace]);

  const renderTransactionList = (records: TransactionRecord[]) => {
    const latestRecords = getLatestRecords(records);

    if (latestRecords.length === 0) {
      return <Typography>No transactions found</Typography>;
    }

    return latestRecords.map((record) => (
      <Grid
        container
        spacing={2}
        key={record._time}
        style={{ marginBottom: "8px" }}
      >
        <Grid item xs={4}>
          <Typography variant="body2">
            {formatDateTime(record._time)}
          </Typography>
        </Grid>
        <Grid item xs={4}>
          <Typography variant="body2">{record.check_name}</Typography>
        </Grid>

        <Grid item xs={4}>
          <Typography variant="body2">
            ${(record._value / 100.0).toFixed(2)}
          </Typography>
        </Grid>
      </Grid>
    ));
  };

  return (
    <ExpansionPanel>
      <ExpansionPanelSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="button">Recent Transactions</Typography>
      </ExpansionPanelSummary>
      <ExpansionPanelDetails>
        <Box width="100%">
          {transactionData.loading ? (
            <Typography>Loading transaction data...</Typography>
          ) : transactionData.error ? (
            <Typography color="error">{transactionData.error}</Typography>
          ) : (
            <>
              <Box mb={3}>
                <Typography variant="h6" gutterBottom>
                  Ticket Sold
                </Typography>
                <Grid container spacing={2} style={{ marginBottom: "8px" }}>
                  <Grid item xs={4}>
                    <Typography variant="subtitle2">Time</Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="subtitle2">
                      Transaction Type
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="subtitle2">Amount</Typography>
                  </Grid>
                </Grid>
                {renderTransactionList(transactionData.ticketSold)}
              </Box>

              <Divider style={{ margin: "24px 0" }} />

              <Box>
                <Typography variant="h6" gutterBottom>
                  Voucher Cashed
                </Typography>
                <Grid container spacing={2} style={{ marginBottom: "8px" }}>
                  <Grid item xs={4}>
                    <Typography variant="subtitle2">Time</Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="subtitle2">
                      Transaction Type
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="subtitle2">Amount</Typography>
                  </Grid>
                </Grid>
                {renderTransactionList(transactionData.voucherCashed)}
              </Box>
            </>
          )}
        </Box>
      </ExpansionPanelDetails>
    </ExpansionPanel>
  );
}
